import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link as RouterLink, Navigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Link
} from '@mui/material';
import { AUTH_API_URL } from '../config/api';
import { useLoginOptions } from '../hooks/useLoginOptions';

const ResetPasswordPage = () => {
  const { loaded: optionsLoaded, passwordLoginEnabled } = useLoginOptions();
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  // 'checking' | 'valid' | 'invalid'
  const [tokenStatus, setTokenStatus] = useState('checking');

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!uid || !token) {
        if (!cancelled) setTokenStatus('invalid');
        return;
      }
      try {
        const response = await axios.get(
          `${AUTH_API_URL}reset-password/validate/${uid}/${token}/`
        );
        if (!cancelled) {
          setTokenStatus(response.data?.valid ? 'valid' : 'invalid');
        }
      } catch (_) {
        if (!cancelled) setTokenStatus('invalid');
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [uid, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setError(null);

    if (!uid || !token) {
      setError('Invalid reset link.');
      return;
    }
    if (!newPassword || !confirmPassword) {
      setError('Please fill in both password fields.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    setLoading(true);
    try {
      try {
        await axios.get(`${AUTH_API_URL}csrf/`, { withCredentials: true });
      } catch (_) {
        // non-fatal
      }
      await axios.post(`${AUTH_API_URL}reset-password/`, {
        uid,
        token,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          'Could not reset password. The link may have expired.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Password sign-in is off: members use Google, so there is nothing to reset here.
  if (optionsLoaded && !passwordLoginEnabled) return <Navigate to="/login" replace />;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        // Phones: flex-start + the card's auto margins still centre it, but let
        // the page scroll (soft keyboard) instead of clipping the card's top.
        alignItems: { xs: 'flex-start', sm: 'center' },
        overflowY: { xs: 'auto', sm: 'visible' },
        px: { xs: 2, sm: 0 },
        py: { xs: 2, sm: 0 },
        bgcolor: theme.palette.background.default,
        zIndex: 1300
      }}
    >
      <Paper
        component="form"
        onSubmit={handleSubmit}
        elevation={6}
        sx={{
          p: { xs: 3, sm: 4 },
          maxWidth: 420,
          width: { xs: '100%', sm: '90%' },
          my: { xs: 'auto', sm: 0 },
          borderRadius: 2,
          // Phones: 16px inputs stop iOS zooming on focus; taller link tap target.
          [theme.breakpoints.down('sm')]: {
            '& .MuiInputBase-input': { fontSize: 16 },
            '& .MuiLink-root': { display: 'inline-block', py: 1 },
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
          <img src="/gva_logo_v2.png" alt="GVA" style={{ height: '3.5rem', width: 'auto' }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography variant="h5" sx={{ fontWeight: 500, lineHeight: 1.2 }}>Time</Typography>
            <Typography variant="h5" sx={{ fontWeight: 500, lineHeight: 1.2 }}>Tracker</Typography>
          </Box>
        </Box>

        <Typography variant="h6" align="center" sx={{ mb: 1 }}>
          Set a new password
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
          Choose a new password for your account. Minimum 8 characters.
        </Typography>

        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Password reset successful. Redirecting you to the login page…
          </Alert>
        ) : tokenStatus === 'checking' ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : tokenStatus === 'invalid' ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            This reset link is invalid or has expired. Please request a new one.
          </Alert>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <TextField
              label="New password"
              type="password"
              variant="outlined"
              fullWidth
              margin="normal"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              autoFocus
              disabled={loading}
            />
            <TextField
              label="Confirm new password"
              type="password"
              variant="outlined"
              fullWidth
              margin="normal"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 2, mb: 2, py: 1.5 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} sx={{ color: theme.palette.primary.contrastText }} /> : 'Reset password'}
            </Button>
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Link
            component={RouterLink}
            to="/login"
            variant="body2"
            >
            Back to login
          </Link>
        </Box>
      </Paper>
    </Box>
  );
};

export default ResetPasswordPage;
